import { Outlet, Link, useLocation } from 'react-router-dom'
import { useUser } from '../hooks/useUser'
import { useTheme } from '../hooks/useTheme'
import {
  LayoutDashboard,
  Sprout,
  Share2,
  Users,
  ClipboardList,
  ChartNoAxesColumn,
  Key,
  HardDrive,
  Archive,
  Trash2,
  Settings,
  Sun,
  Moon,
  Monitor,
  BookOpen,
  Boxes,
} from 'lucide-react'
import {
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarInset,
  SidebarTrigger,
  SidebarRail,
} from './ui/sidebar'
import { Separator } from './ui/separator'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb'
import { Skeleton } from './ui/skeleton'

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/seeds', label: 'Seeds', icon: Sprout },
  { path: '/users', label: 'Users', icon: Users },
  { path: '/shares', label: 'Shares', icon: Share2 },
  { path: '/gfbundles', label: 'Gfbundles', icon: Boxes },
  { path: '/guides', label: 'Guides', icon: BookOpen },
  { path: '/analytics', label: 'Analytics', icon: ChartNoAxesColumn },
  { path: '/audit', label: 'Audit', icon: ClipboardList },
  { path: '/geo', label: 'Geo', icon: Key },
  { path: '/d1', label: 'DB Browser', icon: HardDrive },
  { path: '/r2', label: 'R2 Browser', icon: Archive },
  { path: '/cleanup', label: 'Cleanup', icon: Trash2 },
  { path: '/settings', label: 'Settings', icon: Settings },
]

function isActive(path: string, pathname: string) {
  if (path === '/') return pathname === '/'
  return pathname.startsWith(path)
}

function getBreadcrumbs(pathname: string) {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return [{ label: 'Dashboard', href: '/' }]

  const crumbs: { label: string; href?: string }[] = []

  // Map first segment to nav label
  const firstSeg = segments[0]
  const navItem = navItems.find(n => n.path === `/${firstSeg}`)
  if (navItem) {
    crumbs.push({ label: navItem.label, href: navItem.path })
  } else if (firstSeg === 'seeds') {
    crumbs.push({ label: 'Seeds', href: '/' })
  }

  // Add remaining segments as breadcrumbs
  for (let i = 1; i < segments.length; i++) {
    const href = '/' + segments.slice(0, i + 1).join('/')
    const label = decodeURIComponent(segments[i])
    if (i === segments.length - 1) {
      crumbs.push({ label })
    } else {
      crumbs.push({ label, href })
    }
  }

  return crumbs
}

const themeOptions = [
  { value: 'light' as const, icon: Sun, label: 'Light' },
  { value: 'dark' as const, icon: Moon, label: 'Dark' },
  { value: 'system' as const, icon: Monitor, label: 'System' },
]

export function Layout() {
  const { user, loading } = useUser()
  const { theme, setTheme } = useTheme()
  const location = useLocation()
  const breadcrumbs = getBreadcrumbs(location.pathname)

  return (
    <SidebarProvider>
      <Sidebar variant="inset">
        <SidebarHeader>
          <div className="flex items-center gap-2 px-2 py-1.5">
            <svg className="w-7 h-7 shrink-0" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" fill="#16a34a" />
              <path d="M50 25 C35 40 35 60 50 75 C65 60 65 40 50 25" fill="#dcfce7" />
              <path d="M50 30 L50 70" stroke="#166534" strokeWidth="3" fill="none" />
            </svg>
            <span className="font-semibold text-lg">Seeds Console</span>
          </div>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.path, location.pathname)}
                      tooltip={item.label}
                    >
                      <Link to={item.path}>
                        <item.icon className="size-4" />
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <div className="px-2 py-1.5 space-y-3">
            <div className="flex items-center rounded-md bg-muted/50 p-0.5">
              {themeOptions.map(({ value, icon: Icon, label }) => (
                <button
                  key={value}
                  onClick={() => setTheme(value)}
                  className={`flex-1 flex items-center justify-center gap-1.5 rounded px-2 py-1 text-xs transition-colors ${
                    theme === value
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  title={label}
                >
                  <Icon className="size-3.5" />
                  <span className="hidden group-data-[state=expanded]:inline">{label}</span>
                </button>
              ))}
            </div>
            {loading ? (
              <Skeleton className="h-4 w-32" />
            ) : user ? (
              <span className="text-sm text-muted-foreground truncate block">{user.email}</span>
            ) : null}
            <span className="text-[10px] text-muted-foreground/50">v{__PLATFORM_VERSION__}</span>
          </div>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      {/* min-w-0: a flex item otherwise refuses to shrink below its content, so
          one wide table widened the whole page instead of scrolling inside its
          own card (which is what its sticky first and last columns are for). */}
      <SidebarInset className="min-w-0">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              {breadcrumbs.map((crumb, i) => (
                <BreadcrumbItem key={i}>
                  {i > 0 && <BreadcrumbSeparator />}
                  {crumb.href && i < breadcrumbs.length - 1 ? (
                    <BreadcrumbLink asChild>
                      <Link to={crumb.href}>{crumb.label}</Link>
                    </BreadcrumbLink>
                  ) : (
                    <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                  )}
                </BreadcrumbItem>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </header>
        <div className="flex-1">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
